import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { Menu, X, Download } from 'lucide-react';
import { navItems, sectionIds } from '@/data/navigation';
import { profile } from '@/data/profile';
import { useActiveSection } from '@/hooks/useActiveSection';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { AnchorButton } from '@/components/common/Button';
import { cx } from '@/utils/cn';
import styles from './Header.module.css';

export function Header() {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const menuId = useId();
  const activeId = useActiveSection(sectionIds);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const closeMenu = () => setOpen(false);

  const mobileMenu =
    mounted &&
    createPortal(
      <div
        id={menuId}
        className={cx(styles.mobilePanel, open && styles.mobilePanelOpen)}
        aria-hidden={!open}
        inert={!open ? true : undefined}
      >
        <div className={styles.mobilePanelInner}>
          <nav aria-label="Mobile">
            <ul className={styles.mobileList}>
              {navItems.map((item) => (
                <li key={item.id}>
                  <a
                    href={item.href}
                    className={cx(styles.mobileLink, activeId === item.id && styles.active)}
                    tabIndex={open ? undefined : -1}
                    onClick={closeMenu}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
          <AnchorButton
            href={profile.resumePath}
            download
            tabIndex={open ? undefined : -1}
            onClick={closeMenu}
          >
            <Download size={16} aria-hidden="true" />
            Download Resume
          </AnchorButton>
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      <header className={cx(styles.header, open && styles.headerMenuOpen)}>
        <div className={cx('container', styles.inner)}>
          <a href="#home" className={styles.brand} onClick={closeMenu}>
            <span className={styles.brandMark} aria-hidden="true">
              MSM
            </span>
            <span className={styles.brandText}>{profile.fullName}</span>
          </a>

          <nav className={styles.desktopNav} aria-label="Primary">
            <ul className={styles.navList}>
              {navItems.map((item) => (
                <li key={item.id}>
                  <a
                    href={item.href}
                    className={cx(styles.navLink, activeId === item.id && styles.active)}
                    aria-current={activeId === item.id ? 'true' : undefined}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className={styles.actions}>
            <ThemeToggle />
            <AnchorButton
              href={profile.resumePath}
              variant="secondary"
              size="sm"
              download
              className={styles.resumeDesktop}
            >
              <Download size={16} aria-hidden="true" />
              Resume
            </AnchorButton>
            <button
              type="button"
              className={styles.menuButton}
              aria-expanded={open}
              aria-controls={menuId}
              aria-label={open ? 'Close navigation menu' : 'Open navigation menu'}
              onClick={() => setOpen((value) => !value)}
            >
              {open ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
            </button>
          </div>
        </div>
      </header>
      {mobileMenu}
    </>
  );
}
